import { useMemo, useState } from 'react'
import { ui } from '../../styles/ui'
import { formatEmptyValue } from '../../utils/displayValue'

export default function DataTable({
  columns = [],
  rows = [],
  renderRow,
  emptyText = 'No data',
  loading = false,
  className = '',
  tableClassName = '',
  sortState = null,
  onSortChange = null,
  disableClientSort = false,
}) {
  const [sortConfig, setSortConfig] = useState(null)
  const activeSortConfig = sortState ?? sortConfig

  function getSortValue(row, column) {
    if (typeof column.sortAccessor === 'function') {
      return column.sortAccessor(row)
    }
    if (column.key) {
      return row?.[column.key]
    }
    return null
  }

  function compareValues(left, right) {
    const leftEmpty = left === null || left === undefined || left === ''
    const rightEmpty = right === null || right === undefined || right === ''
    if (leftEmpty && rightEmpty) return 0
    if (leftEmpty) return 1
    if (rightEmpty) return -1

    if (typeof left === 'number' && typeof right === 'number') {
      return left - right
    }

    if (typeof left === 'boolean' && typeof right === 'boolean') {
      return Number(left) - Number(right)
    }

    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  }

  const sortedRows = useMemo(() => {
    if (disableClientSort || !activeSortConfig) return rows
    const column = columns.find((item) => (item.key || item.label) === activeSortConfig.key)
    if (!column) return rows
    const direction = activeSortConfig.direction === 'desc' ? -1 : 1

    return [...rows]
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const leftValue = getSortValue(left.row, column)
        const rightValue = getSortValue(right.row, column)
        const result = compareValues(leftValue, rightValue)
        if (result !== 0) return result * direction
        return left.index - right.index
      })
      .map(({ row }) => row)
  }, [activeSortConfig, columns, disableClientSort, rows])

  function toggleSort(column) {
    if (!column.sortable) return
    const columnKey = column.key || column.label
    const nextSort = (() => {
      const current = activeSortConfig
      if (!current || current.key !== columnKey) {
        return { key: columnKey, direction: column.defaultSortDirection || 'asc' }
      }
      if (current.direction === 'asc') {
        return { key: columnKey, direction: 'desc' }
      }
      if (column.disableSortClear) {
        return { key: columnKey, direction: 'asc' }
      }
      return null
    })()

    if (typeof onSortChange === 'function') {
      onSortChange(nextSort)
      return
    }
    setSortConfig(nextSort)
  }

  function getAriaSort(column) {
    const columnKey = column.key || column.label
    if (!activeSortConfig || activeSortConfig.key !== columnKey) return 'none'
    return activeSortConfig.direction === 'desc' ? 'descending' : 'ascending'
  }

  function getSortIndicator(column) {
    const ariaSort = getAriaSort(column)
    if (ariaSort === 'descending') return '\u2193'
    if (ariaSort === 'ascending') return '\u2191'
    return '\u2195'
  }

  return (
    <div className={`${ui.tables.wrapper} ${className}`.trim()}>
      <table className={`${ui.tables.table} ${tableClassName}`.trim()}>
        <thead className={ui.tables.head}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key || column.label}
                className={`${ui.tables.headCell} ${column.className || ''} ${column.sortable ? 'cursor-pointer select-none' : ''}`.trim()}
                aria-sort={column.sortable ? getAriaSort(column) : undefined}
              >
                {column.sortable ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 text-left"
                    onClick={() => toggleSort(column)}
                  >
                    <span className="min-w-0 truncate">{column.label}</span>
                    <span
                      className="ml-auto flex w-4 shrink-0 items-center justify-center text-xs font-semibold text-muted-foreground"
                      aria-hidden="true"
                    >
                      {getSortIndicator(column)}
                    </span>
                  </button>
                ) : (
                  column.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={ui.tables.body}>
          {loading ? (
            <tr className={ui.tables.row}>
              <td colSpan={Math.max(columns.length, 1)} className={ui.tables.emptyRow}>Loading...</td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={Math.max(columns.length, 1)} className={ui.tables.emptyRow}>{emptyText}</td>
            </tr>
          ) : renderRow ? sortedRows.map((row, index) => renderRow(row, index)) : sortedRows.map((row, index) => (
            <tr key={row?.id ?? index} className={ui.tables.row}>
              {columns.map((column) => {
                if (typeof column.render === 'function') {
                  return (
                    <td
                      key={column.key || column.label}
                      className={`${ui.tables.cellText} ${column.className || ''}`.trim()}
                    >
                      {column.render(row, index)}
                    </td>
                  )
                }

                const rawValue = row?.[column.key]
                return (
                  <td
                    key={column.key || column.label}
                    className={`${ui.tables.cellText} ${column.className || ''}`.trim()}
                  >
                    {formatEmptyValue(rawValue)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
